import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { accountingApi, formatInr, moneyNum } from "@/api/accounting";
import { useAccountingHospitalContext } from "./AccountingHospitalContext";

export default function AccountingBankPharmacyPage() {
  const { hospitalId, readOnly } = useAccountingHospitalContext();
  const qc = useQueryClient();
  const [accountId, setAccountId] = useState("");
  const [newName, setNewName] = useState("");
  const [csv, setCsv] = useState("");
  const [statementDate, setStatementDate] = useState(
    new Date().toISOString().slice(0, 10)
  );
  const [statusFilter, setStatusFilter] = useState("UNMATCHED");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  const statusQ = useQuery({
    queryKey: ["accounting-bank-status", hospitalId],
    queryFn: () => accountingApi.bankReconStatus(hospitalId),
    enabled: !!hospitalId,
  });

  const accountsQ = useQuery({
    queryKey: ["accounting-bank-accounts", hospitalId],
    queryFn: () => accountingApi.listBankAccounts(hospitalId),
    enabled: !!hospitalId,
  });

  const linesQ = useQuery({
    queryKey: [
      "accounting-bank-lines",
      hospitalId,
      accountId,
      statusFilter,
      fromDate,
      toDate,
    ],
    queryFn: () =>
      accountingApi.listBankStatementLines(hospitalId, {
        bankAccountId: accountId || undefined,
        status: statusFilter === "ALL" ? undefined : statusFilter,
        fromDate: fromDate || undefined,
        toDate: toDate || undefined,
      }),
    enabled: !!hospitalId,
  });

  const invQ = useQuery({
    queryKey: ["accounting-pharma-inv", hospitalId, fromDate, toDate],
    queryFn: () =>
      accountingApi.pharmacyInventorySummary(hospitalId, {
        fromDate: fromDate || undefined,
        toDate: toDate || undefined,
      }),
    enabled: !!hospitalId,
  });

  const createAcct = useMutation({
    mutationFn: () =>
      accountingApi.createBankAccount({ name: newName.trim() }, hospitalId),
    onSuccess: () => {
      setNewName("");
      qc.invalidateQueries({ queryKey: ["accounting-bank-accounts"] });
      qc.invalidateQueries({ queryKey: ["accounting-bank-status"] });
    },
  });

  const importStmt = useMutation({
    mutationFn: () =>
      accountingApi.importBankStatement(
        {
          bankAccountId: accountId,
          statementDate,
          csv,
          sourceFilename: "paste.csv",
          autoMatch: true,
        },
        hospitalId
      ),
    onSuccess: () => {
      setCsv("");
      qc.invalidateQueries({ queryKey: ["accounting-bank-lines"] });
      qc.invalidateQueries({ queryKey: ["accounting-bank-status"] });
    },
  });

  const autoMatch = useMutation({
    mutationFn: () => accountingApi.autoMatchBank(accountId, hospitalId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["accounting-bank-lines"] });
      qc.invalidateQueries({ queryKey: ["accounting-bank-status"] });
    },
  });

  const ignoreLine = useMutation({
    mutationFn: (id: string) => accountingApi.ignoreBankStatementLine(id, hospitalId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["accounting-bank-lines"] });
      qc.invalidateQueries({ queryKey: ["accounting-bank-status"] });
    },
  });

  const unmatchLine = useMutation({
    mutationFn: (id: string) => accountingApi.unmatchBankStatementLine(id, hospitalId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["accounting-bank-lines"] });
      qc.invalidateQueries({ queryKey: ["accounting-bank-status"] });
    },
  });

  const accounts = accountsQ.data ?? [];
  const status = statusQ.data;

  return (
    <div className="space-y-8">
      <section className="space-y-4">
        <div>
          <h2 className="text-lg font-medium">Bank reconciliation</h2>
          <p className="mt-1 text-sm text-slate-500">
            Import statement CSV (date, description, reference, debit, credit) and match to
            posted Cash / Bank voucher lines.
          </p>
        </div>

        {status?.note ? (
          <p className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-600">
            {status.note}
          </p>
        ) : null}

        <div className="flex flex-wrap gap-4 text-sm">
          <span>
            Unmatched: <strong>{status?.counts.unmatched ?? "—"}</strong>
          </span>
          <span>
            Matched: <strong>{status?.counts.matched ?? "—"}</strong>
          </span>
          <span>
            Ignored: <strong>{status?.counts.ignored ?? "—"}</strong>
          </span>
          <span>
            Unmatched Dr {formatInr(status?.unmatchedDebit ?? 0)} / Cr{" "}
            {formatInr(status?.unmatchedCredit ?? 0)}
          </span>
        </div>

        {!readOnly ? (
          <div className="flex flex-wrap items-end gap-2">
            <div className="w-56 space-y-1">
              <Label className="text-xs">New bank account name</Label>
              <Input value={newName} onChange={(e) => setNewName(e.target.value)} />
            </div>
            <Button
              type="button"
              disabled={!newName.trim() || createAcct.isPending}
              onClick={() => createAcct.mutate()}
            >
              Add account
            </Button>
          </div>
        ) : null}

        <div className="flex flex-wrap gap-3">
          <div className="w-56 space-y-1">
            <Label className="text-xs">Bank account</Label>
            <Select value={accountId || undefined} onValueChange={setAccountId}>
              <SelectTrigger>
                <SelectValue placeholder="Select account" />
              </SelectTrigger>
              <SelectContent>
                {accounts.map((a) => (
                  <SelectItem key={a.id} value={a.id}>
                    {a.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="w-40 space-y-1">
            <Label className="text-xs">Statement date</Label>
            <Input
              type="date"
              value={statementDate}
              onChange={(e) => setStatementDate(e.target.value)}
            />
          </div>
          <div className="w-36 space-y-1">
            <Label className="text-xs">Line status</Label>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="UNMATCHED">Unmatched</SelectItem>
                <SelectItem value="MATCHED">Matched</SelectItem>
                <SelectItem value="IGNORED">Ignored</SelectItem>
                <SelectItem value="ALL">All</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {!readOnly ? (
          <div className="space-y-2">
            <Label className="text-xs">Paste CSV</Label>
            <Textarea
              rows={5}
              value={csv}
              onChange={(e) => setCsv(e.target.value)}
              placeholder="date,description,reference,debit,credit"
              className="font-mono text-xs"
            />
            <div className="flex flex-wrap gap-2">
              <Button
                type="button"
                disabled={!accountId || !csv.trim() || importStmt.isPending}
                onClick={() => importStmt.mutate()}
              >
                Import + auto-match
              </Button>
              <Button
                type="button"
                variant="outline"
                disabled={!accountId || autoMatch.isPending}
                onClick={() => autoMatch.mutate()}
              >
                Re-run auto-match
              </Button>
            </div>
            {importStmt.isError ? (
              <p className="text-sm text-red-600">Import failed</p>
            ) : null}
          </div>
        ) : null}

        <div className="overflow-x-auto rounded-md border border-slate-200">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Date</TableHead>
                <TableHead>Description</TableHead>
                <TableHead className="text-right">Debit</TableHead>
                <TableHead className="text-right">Credit</TableHead>
                <TableHead>Status</TableHead>
                <TableHead />
              </TableRow>
            </TableHeader>
            <TableBody>
              {(linesQ.data ?? []).map((line) => (
                <TableRow key={line.id}>
                  <TableCell className="whitespace-nowrap text-sm">
                    {String(line.txnDate).slice(0, 10)}
                  </TableCell>
                  <TableCell className="max-w-xs truncate text-sm">
                    {line.description || line.reference || "—"}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {formatInr(moneyNum(line.debit))}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {formatInr(moneyNum(line.credit))}
                  </TableCell>
                  <TableCell className="text-sm">
                    <div>{line.status}</div>
                    {line.matchNote ? (
                      <div className="text-xs text-slate-400">{line.matchNote}</div>
                    ) : null}
                  </TableCell>
                  <TableCell className="text-right">
                    {!readOnly && line.status === "UNMATCHED" ? (
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        onClick={() => ignoreLine.mutate(line.id)}
                      >
                        Ignore
                      </Button>
                    ) : null}
                    {!readOnly && line.status === "MATCHED" ? (
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        onClick={() => unmatchLine.mutate(line.id)}
                      >
                        Unmatch
                      </Button>
                    ) : null}
                  </TableCell>
                </TableRow>
              ))}
              {!linesQ.isLoading && !(linesQ.data ?? []).length ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-sm text-slate-500">
                    No statement lines for this filter.
                  </TableCell>
                </TableRow>
              ) : null}
            </TableBody>
          </Table>
        </div>
      </section>

      <section className="space-y-3">
        <div>
          <h2 className="text-lg font-medium">Pharmacy inventory (books)</h2>
          <p className="mt-1 text-sm text-slate-500">
            Posted 1150 inventory roll-forward and 5110 COGS from stock-in / issue hooks.
          </p>
        </div>
        <div className="flex flex-wrap gap-3">
          <div className="w-44 space-y-1">
            <Label className="text-xs">From</Label>
            <Input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
          </div>
          <div className="w-44 space-y-1">
            <Label className="text-xs">To</Label>
            <Input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} />
          </div>
        </div>
        {invQ.data?.note ? (
          <p className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-600">
            {invQ.data.note}
          </p>
        ) : null}
        <div className="grid gap-3 sm:grid-cols-3">
          <div className="rounded-md border border-slate-200 px-3 py-2">
            <div className="text-xs text-slate-500">Purchases (Dr 1150)</div>
            <div className="text-lg tabular-nums">{formatInr(invQ.data?.purchases ?? 0)}</div>
          </div>
          <div className="rounded-md border border-slate-200 px-3 py-2">
            <div className="text-xs text-slate-500">COGS (Dr 5110)</div>
            <div className="text-lg tabular-nums">{formatInr(invQ.data?.cogs ?? 0)}</div>
          </div>
          <div className="rounded-md border border-slate-200 px-3 py-2">
            <div className="text-xs text-slate-500">Closing books (1150)</div>
            <div className="text-lg tabular-nums">{formatInr(invQ.data?.closingBooks ?? 0)}</div>
          </div>
        </div>
      </section>
    </div>
  );
}
